import React, { useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Demo responses keyed on keywords in the submitted code
// ---------------------------------------------------------------------------
const DEMO_RESPONSES: Array<{ match: RegExp; output: string | ((code: string) => string) }> = [
  {
    match: /df\.head|df\.tail/,
    output: `   session_id  soc_start  soc_end  energy_kwh  duration_min
0  abc123      20         80       36.0        120
1  def456      35         90       33.0        110
2  ghi789      10         75       39.0        130
3  jkl012      50         95       27.0         90
4  mno345      25         85       36.0        120`,
  },
  {
    match: /df\.describe/,
    output: `       soc_start    soc_end  energy_kwh  duration_min
count   5000.000   5000.000    5000.000      5000.000
mean      31.420     82.310      30.540       101.200
std       14.230     12.110       8.920        28.440
min        5.000     55.000       6.000        20.000
25%       20.000     74.000      24.000        80.000
50%       30.000     83.000      30.000       100.000
75%       42.000     92.000      37.000       120.000
max       75.000    100.000      60.000       240.000`,
  },
  {
    match: /groupby|value_counts|mean\(\)|sum\(\)/,
    output: `market_segment
drayage_HDT            1842
long_haul_HDT          1530
regional_home_HDT      1109
regional_nonhome_HDT    519
dtype: int64`,
  },
  {
    match: /plt\.|plot\(|figure\(/,
    output: `[Figure rendered: matplotlib chart displayed inline]\n\n📊 Plot generated successfully (demo mode — no canvas in browser)`,
  },
  {
    match: /import pandas|import numpy|import matplotlib/,
    output: ``,   // silent import
  },
  {
    match: /print\((.+)\)/,
    output: (code: string) => {
      const m = code.match(/print\((.+)\)/);
      const inner = m ? m[1].replace(/['"]/g, '') : '...';
      return inner;
    },
  },
  {
    match: /len\(|shape/,
    output: `(5000, 17)`,
  },
  {
    match: /read_csv/,
    output: `✓ Loaded charging_sessions.csv — 5000 rows × 17 columns`,
  },
];

// ---------------------------------------------------------------------------
// Python syntax highlighter  (no external deps)
// ---------------------------------------------------------------------------
const C = {
  kw:      'color:#cba6f7',             // purple  – keywords
  str:     'color:#a6e3a1',             // green   – strings
  comment: 'color:#585b70;font-style:italic', // muted – comments
  num:     'color:#fab387',             // peach   – numbers
  builtin: 'color:#89b4fa',             // blue    – builtins
  op:      'color:#89dceb',             // sky     – operators
  dec:     'color:#f38ba8',             // red     – decorators
  plain:   'color:#cdd6f4',             // base    – plain text
};

const KW = new Set([
  'def','class','import','from','return','if','elif','else','for','while',
  'in','not','and','or','is','True','False','None','try','except','finally',
  'with','as','lambda','yield','async','await','pass','break','continue',
  'raise','assert','del','global','nonlocal',
]);

const BUILTINS = new Set([
  'print','len','range','list','dict','set','tuple','str','int','float',
  'bool','type','isinstance','hasattr','getattr','setattr','enumerate','zip',
  'map','filter','sorted','reversed','any','all','sum','min','max','abs',
  'round','open','super','object','property','staticmethod','classmethod',
  'pd','plt','np','df',
]);

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function span(style: string, text: string): string {
  return `<span style="${style}">${esc(text)}</span>`;
}

function highlightPython(raw: string): string {
  const out: string[] = [];
  let i = 0;
  const n = raw.length;

  while (i < n) {
    const ch = raw[i];

    // Decorator  @foo
    if (ch === '@' && (i === 0 || raw[i - 1] === '\n')) {
      let j = i + 1;
      while (j < n && /\w/.test(raw[j])) j++;
      out.push(span(C.dec, raw.slice(i, j)));
      i = j;
      continue;
    }

    // Triple-quoted string  """ or '''
    if ((ch === '"' || ch === "'") && raw.slice(i, i + 3) === ch.repeat(3)) {
      const q = ch.repeat(3);
      const end = raw.indexOf(q, i + 3);
      const str = end === -1 ? raw.slice(i) : raw.slice(i, end + 3);
      out.push(span(C.str, str));
      i += str.length;
      continue;
    }

    // Single-line string  " or '
    if (ch === '"' || ch === "'") {
      let j = i + 1;
      while (j < n && raw[j] !== ch && raw[j] !== '\n') {
        if (raw[j] === '\\') j++;
        j++;
      }
      out.push(span(C.str, raw.slice(i, j + 1)));
      i = j + 1;
      continue;
    }

    // Comment  # ...
    if (ch === '#') {
      const end = raw.indexOf('\n', i);
      const comment = end === -1 ? raw.slice(i) : raw.slice(i, end);
      out.push(span(C.comment, comment));
      i += comment.length;
      continue;
    }

    // Number
    if (/\d/.test(ch) && (i === 0 || /[\s\W]/.test(raw[i - 1]))) {
      let j = i;
      while (j < n && /[\d._e]/.test(raw[j])) j++;
      out.push(span(C.num, raw.slice(i, j)));
      i = j;
      continue;
    }

    // Identifier / keyword / builtin
    if (/[a-zA-Z_]/.test(ch)) {
      let j = i;
      while (j < n && /\w/.test(raw[j])) j++;
      const word = raw.slice(i, j);
      if (KW.has(word)) out.push(span(C.kw, word));
      else if (BUILTINS.has(word)) out.push(span(C.builtin, word));
      else out.push(esc(word));
      i = j;
      continue;
    }

    // Operator
    if (/[+\-*/%=<>!&|^~]/.test(ch)) {
      out.push(span(C.op, ch));
      i++;
      continue;
    }

    // Everything else (parens, commas, spaces, newlines)
    out.push(esc(ch));
    i++;
  }

  return out.join('');
}

function evaluate(code: string): string {
  for (const rule of DEMO_RESPONSES) {
    if (rule.match.test(code)) {
      return typeof rule.output === 'function' ? rule.output(code) : rule.output;
    }
  }
  return `[Demo] Executed successfully (no output)`;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface Cell {
  id: number;
  code: string;
  output: string | null;
  status: 'idle' | 'running' | 'done' | 'error';
}

const STARTER_CODE = `import pandas as pd
import matplotlib.pyplot as plt

df = pd.read_csv('charging_sessions.csv')
df.head()`;

let nextId = 2;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export const MarimoFrame: React.FC = () => {
  const [cells, setCells] = useState<Cell[]>([
    { id: 1, code: STARTER_CODE, output: null, status: 'idle' },
  ]);

  const run = (id: number) => {
    setCells(prev =>
      prev.map(c => (c.id === id ? { ...c, status: 'running', output: null } : c))
    );
    setTimeout(() => {
      setCells(prev =>
        prev.map(c => {
          if (c.id !== id) return c;
          const out = evaluate(c.code);
          return { ...c, status: 'done', output: out };
        })
      );
    }, 420);
  };

  const addCell = () => {
    setCells(prev => [...prev, { id: nextId++, code: '', output: null, status: 'idle' }]);
  };

  const deleteCell = (id: number) => {
    setCells(prev => prev.filter(c => c.id !== id));
  };

  const updateCode = (id: number, code: string) => {
    setCells(prev => prev.map(c => (c.id === id ? { ...c, code } : c)));
  };

  return (
    <div style={s.root}>
      <div style={s.toolbar}>
        <span style={s.toolbarTitle}>Python Console</span>
        <span style={s.badge}>demo</span>
        <div style={s.spacer} />
        <button style={s.btnSecondary} onClick={() => cells.forEach(c => run(c.id))}>
          ▶▶ Run All
        </button>
        <button style={s.btnSecondary} onClick={addCell}>+ Cell</button>
      </div>

      <div style={s.cellList}>
        {cells.map((cell, idx) => (
          <CellView
            key={cell.id}
            cell={cell}
            index={idx}
            onRun={() => run(cell.id)}
            onDelete={() => deleteCell(cell.id)}
            onChange={code => updateCode(cell.id, code)}
          />
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Cell sub-component
// ---------------------------------------------------------------------------
interface CellViewProps {
  cell: Cell;
  index: number;
  onRun: () => void;
  onDelete: () => void;
  onChange: (code: string) => void;
}

const CellView: React.FC<CellViewProps> = ({ cell, index, onRun, onDelete, onChange }) => {
  const taRef  = useRef<HTMLTextAreaElement>(null);
  const preRef = useRef<HTMLPreElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      onRun();
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      const ta  = taRef.current!;
      const pos = ta.selectionStart;
      const val = ta.value;
      onChange(val.slice(0, pos) + '    ' + val.slice(ta.selectionEnd));
      requestAnimationFrame(() => { ta.selectionStart = ta.selectionEnd = pos + 4; });
    }
  };

  // Sync pre scroll to textarea scroll so highlighted layer stays in lockstep
  const handleScroll = () => {
    if (preRef.current && taRef.current) {
      preRef.current.scrollTop  = taRef.current.scrollTop;
      preRef.current.scrollLeft = taRef.current.scrollLeft;
    }
  };

  const lines  = cell.code.split('\n').length;
  const height = Math.max(72, Math.min(lines * 20 + 12, 320));

  // Shared font/spacing applied to both layers
  const editorBase: React.CSSProperties = {
    position:   'absolute',
    top: 0, left: 0, right: 0,
    height,
    margin:     0,
    padding:    '8px 10px',
    font:       'inherit',
    fontSize:   '0.8rem',
    lineHeight: '1.6',
    whiteSpace: 'pre-wrap',
    wordBreak:  'break-word',
    overflowWrap: 'break-word',
    boxSizing:  'border-box',
    tabSize:    4,
  };

  return (
    <div style={s.cell}>
      {/* gutter */}
      <div style={s.gutter}>
        <span style={s.cellIdx}>In [{index + 1}]:</span>
        <button style={s.runBtn} title="Run (Ctrl+Enter)" onClick={onRun}>
          {cell.status === 'running' ? '⏳' : '▶'}
        </button>
      </div>

      {/* editor */}
      <div style={s.cellBody}>
        {/* highlighted layer (pointer-events off, hidden overflow synced via JS) */}
        <div style={{ position: 'relative', height, borderRadius: 6, overflow: 'hidden', border: '1px solid #313244', background: '#181825' }}>
          <pre
            ref={preRef}
            aria-hidden
            style={{ ...editorBase, overflow: 'hidden', color: '#cdd6f4', pointerEvents: 'none' }}
            dangerouslySetInnerHTML={{ __html: highlightPython(cell.code) + '\n' }}
          />
          {/* transparent textarea on top captures all input */}
          <textarea
            ref={taRef}
            value={cell.code}
            onChange={e => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onScroll={handleScroll}
            style={{
              ...editorBase,
              background:   'transparent',
              color:        'transparent',
              caretColor:   '#cba6f7',
              resize:       'none',
              outline:      'none',
              border:       'none',
              overflow:     'auto',
              zIndex:       1,
            }}
            spellCheck={false}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
          />
        </div>

        {/* output */}
        {cell.output !== null && (
          <div style={s.outputWrap}>
            <span style={s.outLabel}>Out [{index + 1}]:</span>
            <pre style={s.output}>{cell.output}</pre>
          </div>
        )}
      </div>

      {/* delete */}
      <button style={s.delBtn} title="Delete cell" onClick={onDelete}>✕</button>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    background: '#1e1e2e',
    fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", monospace',
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 12px',
    background: '#181825',
    borderBottom: '1px solid #313244',
    flexShrink: 0,
  },
  toolbarTitle: {
    color: '#cdd6f4',
    fontWeight: 700,
    fontSize: '0.8rem',
    letterSpacing: '0.05em',
  },
  badge: {
    background: '#313244',
    color: '#a6e3a1',
    borderRadius: 4,
    padding: '1px 6px',
    fontSize: '0.65rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  spacer: { flex: 1 },
  btnSecondary: {
    background: '#313244',
    color: '#cdd6f4',
    border: 'none',
    borderRadius: 6,
    padding: '4px 10px',
    fontSize: '0.72rem',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontWeight: 600,
  },
  cellList: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px 0',
  },
  cell: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 0,
    borderBottom: '1px solid #313244',
    padding: '8px 0',
  },
  gutter: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    padding: '4px 8px',
    width: 56,
    flexShrink: 0,
  },
  cellIdx: {
    color: '#6c7086',
    fontSize: '0.67rem',
    whiteSpace: 'nowrap',
  },
  runBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#a6e3a1',
    fontSize: '0.85rem',
    padding: 2,
    lineHeight: 1,
  },
  cellBody: {
    flex: 1,
    minWidth: 0,
    paddingRight: 8,
  },
  outputWrap: {
    marginTop: 4,
    display: 'flex',
    gap: 8,
    alignItems: 'flex-start',
  },
  outLabel: {
    color: '#6c7086',
    fontSize: '0.67rem',
    whiteSpace: 'nowrap',
    paddingTop: 4,
    width: 48,
    flexShrink: 0,
  },
  output: {
    flex: 1,
    margin: 0,
    padding: '6px 10px',
    background: '#11111b',
    color: '#a6e3a1',
    borderLeft: '2px solid #313244',
    borderRadius: 4,
    fontSize: '0.75rem',
    lineHeight: 1.55,
    overflowX: 'auto',
    fontFamily: 'inherit',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  delBtn: {
    background: 'transparent',
    border: 'none',
    color: '#45475a',
    cursor: 'pointer',
    fontSize: '0.75rem',
    padding: '4px 8px',
    flexShrink: 0,
    lineHeight: 1,
  },
};